/* eslint-disable @typescript-eslint/no-explicit-any */
import { Redirect, Route, RouteComponentProps } from 'react-router';
import { Switch } from 'react-router-dom';

import { IWithContextProps, withContext } from '@tock/coretypes';
import { Page } from 'components/ui-flow/uiFlowComponent';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ProtectedRoute from 'routes/ProtectedRoute';
import TransferConfirmRoute, {
  Props as TransferConfirmRouteProps,
} from 'routes/protected/account/transfer/TransferConfirmRoute';
import TransferPriceRoute, {
  Props as TransferPriceRouteProps,
} from 'routes/protected/account/transfer/TransferPriceRoute';
import TransferRecipientRoute, {
  Props as TransferRecipientRouteProps,
} from 'routes/protected/account/transfer/TransferRecipientRoute';
import TransferRoute, {
  Props as TransferRouteProps,
} from 'routes/protected/account/transfer/TransferRoute';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import flowRoute from 'components/ui-flow/flowRoute';

const pages = {
  option: {
    path: 'option',
    invalidIf: (props: TransferRouteProps) => {
      return !props.transfer.purchase;
    },
    shouldShow: (props: TransferRouteProps) => {
      const { transfer } = props;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return transfer.purchase.amountPaidCents! > 0;
    },
  },
  recipient: {
    path: 'recipient',
    invalidIf: (props: TransferRecipientRouteProps) => {
      return !props.transfer.purchase;
    },
    shouldShow: () => true,
  },
  price: {
    path: 'price',
    invalidIf: (props: TransferPriceRouteProps) => {
      return !props.transfer.purchase || !props.transfer.transferToEmail;
    },
    shouldShow: (props: TransferPriceRouteProps) => {
      const { transfer } = props;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return !transfer.isGiftFlow && transfer.purchase.amountPaidCents! > 0;
    },
  },
  confirm: {
    path: 'confirm',
    invalidIf: (props: TransferConfirmRouteProps) => {
      return !props.transfer.purchase || !props.transfer.transferToEmail;
    },
    shouldShow: () => true,
  },
};

const pageOrder = [pages.option, pages.recipient, pages.price, pages.confirm];

interface ConnectProps extends DefaultConnectProps {
  transfer: StoreType['transfer'];
}

interface Props {
  currentPage: Page;
  nextPage: Page;
  prevPage: Page;
}

class TransferInitiationFlowRoute extends BaseComponent<
  Props & RouteComponentProps<any> & ConnectProps & IWithContextProps
> {
  componentDidMount() {
    this.props.actions.navigation.set({
      title: 'Transfer',
      hasBack: true,
    });
  }

  renderRedirect() {
    return (
      <Redirect to={{ ...this.props.location, pathname: `${this.props.match.path}/option` }} />
    );
  }

  renderOptions(routeProps: RouteComponentProps<any>) {
    return (
      <TransferRoute
        transfer={this.props.transfer}
        onSetTransferPrice={this.props.actions.transfer.setTransferPrice}
        onSetIsGiftFlow={this.props.actions.transfer.setIsGiftFlow}
        currentPage={this.props.currentPage}
        nextPage={this.props.nextPage}
        prevPage={this.props.prevPage}
        {...routeProps}
      />
    );
  }

  renderRecipients(routeProps: RouteComponentProps<any>) {
    return (
      <TransferRecipientRoute
        onSetTransferTo={this.props.actions.transfer.setTransferTo}
        transfer={this.props.transfer}
        currentPage={this.props.currentPage}
        nextPage={this.props.nextPage}
        prevPage={this.props.prevPage}
        {...routeProps}
      />
    );
  }

  renderPrice(routeProps: RouteComponentProps<any>) {
    return (
      <TransferPriceRoute
        onSetTransferPrice={this.props.actions.transfer.setTransferPrice}
        transfer={this.props.transfer}
        currentPage={this.props.currentPage}
        nextPage={this.props.nextPage}
        prevPage={this.props.prevPage}
        {...routeProps}
      />
    );
  }

  renderConfirm(routeProps: RouteComponentProps<any>) {
    return (
      <TransferConfirmRoute
        onInitiateTransfer={this.props.actions.transfer.initiate}
        transfer={this.props.transfer}
        actions={this.props.actions}
        currentPage={this.props.currentPage}
        nextPage={this.props.nextPage}
        prevPage={this.props.prevPage}
        {...routeProps}
      />
    );
  }

  render() {
    const { app, match } = this.props;

    return (
      <Switch>
        <Route exact path={match.path} render={this.renderRedirect} />
        <ProtectedRoute
          app={app}
          path={`${match.path}/${pages.option.path}`}
          render={this.renderOptions}
        />
        <ProtectedRoute
          app={app}
          path={`${match.path}/${pages.recipient.path}`}
          render={this.renderRecipients}
        />
        <ProtectedRoute
          app={app}
          path={`${match.path}/${pages.price.path}`}
          render={this.renderPrice}
        />
        <ProtectedRoute
          app={app}
          path={`${match.path}/${pages.confirm.path}`}
          render={this.renderConfirm}
        />
      </Switch>
    );
  }
}

export default connect<RouteComponentProps<any> & Props, ConnectProps>(
  withContext(flowRoute(pageOrder)(TransferInitiationFlowRoute)),
  ['app', 'transfer']
);
